package com.hyperether.goodjob.repository.prefs

import com.hyperether.goodjob.models.User
import com.hyperether.resources.AppLocale
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.serialization.json.Json
import org.lighthousegames.logging.logging

class PrefsManager(val prefs: Prefs) {

    val log = logging("PrefsManager")

    companion object {
        const val TOKEN_KEY = "token_key"
        const val REFRESH_TOKEN_KEY = "refresh_token_key"
        const val EMAIL = "email"
        const val IS_ONBOARDING_SHOWN = "is_onboarding_shown"
        const val USER = "user"
        const val WORKSPACE_ID = "workspace_id"
        const val PLAN_ID = "plan_id"
        const val REFERRER = "referrer"
        const val EMPLOYEE_NUMBER_EXCEEDED = "employee_number_exceeded"
        const val MANAGER_NUMBER_EXCEEDED = "manager_number_exceeded"
        const val LANGUAGE_KEY = "current_language"
        const val WID_QUERY = "wid_query"
    }

    fun saveToken(token: String) {
        prefs.putString(TOKEN_KEY, token)
    }

    fun getToken(): String {
        return prefs.getString(TOKEN_KEY)
    }

    fun saveRefreshToken(token: String) {
        prefs.putString(REFRESH_TOKEN_KEY, token)
    }

    fun getRefreshToken(): String {
        return prefs.getString(REFRESH_TOKEN_KEY)
    }

    fun saveEmail(email: String) {
        prefs.putString(EMAIL, email)
    }

    fun getEmail(): String {
        return prefs.getString(EMAIL)
    }

    fun onBoardingShown(isOnboardingShown: Boolean) {
        prefs.putBool(IS_ONBOARDING_SHOWN, isOnboardingShown)
    }

    fun isOnboardingShown(): Boolean {
        return prefs.getBool(IS_ONBOARDING_SHOWN)
    }

    private val _userFlow = MutableStateFlow<User?>(null)
    val userFlow: StateFlow<User?> get() = _userFlow

    init {
        // Initialize userFlow with the current user from prefs
        _userFlow.value = getUser()
    }

    fun saveUser(user: User?) {
        if (user != null) {
            val userString = Json.encodeToString(User.serializer(), user)
            log.d("saveUser") { userString }
            prefs.putString(USER, userString)
        } else {
            prefs.putString(USER, "")
        }
        _userFlow.value = user
    }

    fun deleteUser() {
        log.d("deleteUser") { "User deleted" }
        prefs.putString(USER, "")
        _userFlow.value = null
    }

    fun getUser(): User? {
        val retrievedUserString = prefs.getString(USER)
        return if (retrievedUserString.isNotEmpty()) {
//            log.d("getUser") { retrievedUserString }
            val json = Json { ignoreUnknownKeys = true }
            val user = json.decodeFromString<User>(retrievedUserString)
            user
        } else {
            log.d("getUser") { "User not found in prefs" }
            null
        }
    }

    fun saveWorkspaceId(workspaceId: String) {
        prefs.putString(WORKSPACE_ID, workspaceId)
    }

    fun getWorkspaceId(): String {
        return prefs.getString(WORKSPACE_ID)
    }

    fun removeAll() {
        log.d("Prefs") { "Remove all" }
        prefs.removeAll()
    }

//    fun setFlow(flowType: String) {
//        prefs.putString(FLOW_TYPE, flowType)
//    }
//
//    fun getFlow(): String {
//        return prefs.getString(FLOW_TYPE)
//    }

    fun isReferrerDone(): Boolean {
        return prefs.getBool(REFERRER)
    }

    fun referrerDone() {
        prefs.putBool(REFERRER, true)
    }

    private val _managerNumberExceeded = MutableStateFlow(false)
    val managerNumberExceeded: StateFlow<Boolean> get() = _managerNumberExceeded

    fun setManagerNumberExceeded(newValue: Boolean) {
        _managerNumberExceeded.value = newValue
    }

    private val _employeeNumberExceeded = MutableStateFlow(false)
    val employeeNumberExceeded: StateFlow<Boolean> get() = _employeeNumberExceeded

    fun setEmployeeNumberExceeded(newValue: Boolean) {
        _employeeNumberExceeded.value = newValue
    }

    fun saveLanguage(token: String) {
        prefs.putString(LANGUAGE_KEY, token)
    }

    fun getLanguage(): String {
        return prefs.getString(LANGUAGE_KEY, AppLocale.DEFAULT.code)
    }

    fun setWidQuery(wid: String) {
        prefs.putString(WID_QUERY, wid)
    }

    fun getWidQuery(): String {
        return prefs.getString(WID_QUERY)
    }
}